/**
 * Which call Iris is about — the question the dock has to answer before it can
 * show anything.
 *
 * It lives here rather than beside the component so that editing the panel does
 * not throw away the answer, and so a page can ask without rendering anything.
 * Whether there is ROOM for her column is `useIsLargeScreen`, the shared answer
 * every `lg:`-gated panel on this app uses.
 */

import { useEffect, useId, useRef } from 'react';
import { useActiveCall, type CallEntry } from '@/stores/voip.store';
import { useTranscriptDockStore } from '@/stores/transcript.store';

/**
 * The call Iris is about, held past the end of it, with the shell's own dock
 * stood down for as long as this page is showing it.
 *
 * A page that belongs to ONE call passes it, and then Iris shows that call and
 * no other. Reading the active call itself meant answering a second customer
 * swapped the transcript under a half-filled form — the agent reads B while
 * writing A's job. Passing `null` is meaningful: it says "that call has
 * ended", and the words stay up.
 */
export function useIrisCall(call?: CallEntry | null): CallEntry | null {
  const activeCall = useActiveCall();
  const source = call !== undefined ? call : activeCall;
  const claim = useTranscriptDockStore((state) => state.claim);
  const release = useTranscriptDockStore((state) => state.release);
  // Identity for the claim, so this page can only ever release its own.
  const owner = useId();

  // Held in a ref rather than state: it changes only when a NEW call arrives,
  // and re-rendering on hangup is exactly what we are avoiding.
  const shownRef = useRef<CallEntry | null>(null);
  if (source) shownRef.current = source;
  const shown = source ?? shownRef.current;

  /*
   * The claim follows the LIVE call, not the one we keep showing.
   *
   * One call is never streamed in two places at once, so this page takes the
   * shell's dock for as long as it is streaming — open or collapsed, because
   * the chip is still this page's call. But `shown` outlives the call on
   * purpose, and claiming on THAT held the dock until the page unmounted: a
   * dispatcher still on this job when customer B rang would find B's
   * transcript nowhere, hidden behind customer A's stale claim. Releasing on
   * hangup costs this page nothing — the words stay, they just stop being
   * streamed — and hands the dock back the moment B needs it.
   */
  const hasLiveCall = Boolean(source);
  useEffect(() => {
    if (!hasLiveCall) return;
    claim(owner);
    return () => release(owner);
  }, [claim, hasLiveCall, owner, release]);

  return shown;
}
